<template>
  <!-- Boards Details View -->
  <MainLayout
    v-scroll="onScroll"
    scroll-toolbar
    :bg-color="adView === 'table' ? 'white' : ''"
  >
    <!-- Toolbar -->
    <template #toolbar>
      <div class="flex flex-col w-full">
        <!-- Top header -->
        <div ref="toolbar" class="relative flex items-center justify-between p-3">
          <!-- BOARD ROUTE SWITCHER -->
          <div class="flex items-center transition-opacity" :style="{width: routeSwitcherWidth}"
          :class="loadingRouteSwitcher ? 'opacity-0' : 'opacity-100'">
            <!-- Back button -->
            <button class="p-1.5 text-icon-normal transition-colors hover:text-icon-muted flex-shrink-0"
            @click="navigateBack">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M11.3215 13.3333L8.57742 10.5892C8.25198 10.2638 8.25198 9.73614 8.57742 9.4107L11.3215 6.66663" 
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <!-- Board selector -->
            <RouteSwitcherDropdown 
              :expanded="boardSwitcherExpanded"
              :current-route-item="board"
              :route-options="boardRoutes"
              @expand="boardSwitcherExpanded = true"
              @collapse="boardSwitcherExpanded = false"
            />
          </div>

          <!-- Search Bar -->
          <div class="absolute flex top-1/2 transform -translate-y-1/2 pointer-events-none" style="left: 350px; right: 350px; z-index: 40001">
            <div ref="searchBar" class="relative flex-grow mx-auto min-w-0 h-8 pointer-events-auto" 
            style="max-width: 500px; min-width: 320px">
              <SearchBar 
                :text-search.sync="textSearch"
                :qualifier="board.name"
                keywords-only
                @submit="handleSearchSubmit"
              />
            </div>
          </div>

          <div class="flex items-center gap-2.5">
            <!-- More dropdown -->
            <div class="relative">
              <!-- opener -->
              <button class="flex items-center pl-1.5 py-1.5 pr-1 rounded-md transition-colors duration-100"
              :class="{'bg-neutral-25': showMoreDropdown}" @click="showMoreDropdown = !showMoreDropdown">
                <BaseText type="label" size="sm" class="text-text-muted px-1.5">
                  More
                </BaseText>
                <div class="transform transition-transform" :style="{ transform: showMoreDropdown ? 'scaleY(-1)': 'scaleY(1)'}">
                  <ChevronIcon className="text-icon-normal" />
                </div>
              </button>
              <!-- dropdown -->
              <transition>
                <div v-if="showMoreDropdown" class="more-dropdown flex flex-col gap-0.5 p-1"
                v-on-clickaway="() => { showMoreDropdown = false }">
                  <button v-for="(option, index) in moreDropdownOptions" :key="`more-option-${index}`"
                  class="group flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
                  @click="() => { showMoreDropdown = false; option.action() }">
                    <component :is="option.icon" class="text-icon-normal transition-colors duration-100 group-hover:text-icon-muted"/>
                    <BaseText type="body" size="sm" class="text-text-normal transition-colors duration-100 group-hover:text-text-muted">
                      {{ option.name }}
                    </BaseText>
                  </button>
                </div>
              </transition>
            </div>
            <!-- Create New Dropdown -->
            <BaseCreateNewDropdown 
              position="left"
              @save="fetchAdvertisements(false)" 
            />
            <!-- Share Button -->
            <button class="share-btn flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md"
            @click="copyShareLink">
              <ShareIcon class="text-white" />
              <BaseText type="label" size="sm" class="text-white">
                Share
              </BaseText>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Filter Bar -->
    <template #filter>
      <div ref="filterBar" class="border border-border-normal bg-white bg-opacity-90" style="backdrop-filter: blur(6px)">
        <FilterTopBar
          ref="filterTopBar"
          no-search
          :text-search="textSearch"
          :active-filters.sync="activeFilters"
          :sort-order.sync="sortOrder"
          :selected-view.sync="adView"
          :excluded-filter-types="['boards']"
          @change="fetchAdvertisements"
          @refresh="fetchAdvertisements"
        />
      </div>
    </template>

    <template #content>
      <!-- Loading State -->
      <BaseLoadingLogo v-if="loadingAdvertisements" class="mt-24" />

      <!-- Empty State -->
      <LibraryEmptyState 
        v-else-if="!advertisements.length"
        class="mt-24"
        headerText="This board is empty"
        subheaderText="You created this board but haven't save any ads yet. Save ads from discovery or with the chrome extension."
        :filters-applied="filtersApplied"
        :has-search-query="textSearch.length > 0"
        :max-subheader-width="420"
        @clearFilters="clearFilters"
      />

      <!-- Advertisements List (Grid view or list view - toggled in filter bar) -->
      <AdvertisementList
        v-else-if="adView === 'grid'"
        :advertisements="advertisements"
        :loading="loadingMoreAds"
        :allAdsLoaded="allAdsLoaded"
        @updateAds="updateAds"
        @removeAds="removeAds"
        @getMoreAds="() => getMoreAds({})"
        @reload="fetchAdvertisements(false, true)"
      />
      <AdvertismentTableView
        v-else-if="adView === 'table'"
        :advertisements="advertisements"
        :selected-ads.sync="selectedAds"
        @updateAds="updateAds"
        @removeAds="removeAds"
        @reload="fetchAdvertisements(false)"
      />

      <!-- Bulk Actions Toolbar -->
      <div v-if="selectedAds.length" class="fixed bottom-nav px-4 py-3 rounded-xl flex items-center appear-animation"
      style="z-index: 30000; backdrop-filter: blur(2px);">
        <!-- Selected Number -->
        <div class="flex gap-2 text-text-muted select-none mr-4">
          <div class="rounded-full bg-background-normal w-5 h-5 flex items-center justify-center">
            <BaseText type="label" size="sm">
              {{ selectedAds.length }}
            </BaseText>
          </div>
          <BaseText type="body" size="sm">
            selected
          </BaseText>
        </div>
        <!-- Divider -->
        <div class="h-6 border-r border-border-normal mx-3" />
        <!-- Archive -->
        <BaseButton text @click="showBulkAddBoardsModal = true">
          <div class="flex items-center gap-1.5">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.03502 16.6933L4.37551 16.0251H4.37551L4.03502 16.6933ZM3.30666 15.965L3.97491 15.6245H3.97491L3.30666 15.965ZM16.6933 15.965L16.0251 15.6245L16.0251 15.6245L16.6933 15.965ZM15.965 16.6933L15.6245 16.0251L15.6245 16.0251L15.965 16.6933ZM16.6933 4.03502L16.0251 4.37551L16.0251 4.37551L16.6933 4.03502ZM15.965 3.30666L15.6245 3.97491L15.6245 3.97491L15.965 3.30666ZM3.30666 4.03502L2.6384 3.69452L2.6384 3.69452L3.30666 4.03502ZM4.03502 3.30666L3.69452 2.6384L3.69452 2.6384L4.03502 3.30666ZM10.75 3.125V2.375H9.25V3.125H10.75ZM3.125 7.375H2.375V8.875H3.125V7.375ZM9.25 16.875V17.625H10.75V16.875H9.25ZM16.875 12.625H17.625V11.125H16.875V12.625ZM16.125 5.79167V14.2083H17.625V5.79167H16.125ZM14.2083 16.125H5.79167V17.625H14.2083V16.125ZM3.875 14.2083V5.79167H2.375V14.2083H3.875ZM5.79167 3.875H14.2083V2.375H5.79167V3.875ZM5.79167 16.125C5.31258 16.125 4.99837 16.1244 4.75808 16.1048C4.52654 16.0859 4.43042 16.0531 4.37551 16.0251L3.69452 17.3616C3.99613 17.5153 4.31163 17.5733 4.63593 17.5998C4.95147 17.6256 5.33733 17.625 5.79167 17.625V16.125ZM2.375 14.2083C2.375 14.6627 2.37442 15.0485 2.4002 15.3641C2.42669 15.6884 2.48472 16.0039 2.6384 16.3055L3.97491 15.6245C3.94693 15.5696 3.91413 15.4735 3.89522 15.2419C3.87558 15.0016 3.875 14.6874 3.875 14.2083H2.375ZM4.37551 16.0251C4.20303 15.9372 4.0628 15.797 3.97491 15.6245L2.6384 16.3055C2.87009 16.7602 3.2398 17.1299 3.69452 17.3616L4.37551 16.0251ZM16.125 14.2083C16.125 14.6874 16.1244 15.0016 16.1048 15.2419C16.0859 15.4735 16.0531 15.5696 16.0251 15.6245L17.3616 16.3055C17.5153 16.0039 17.5733 15.6884 17.5998 15.3641C17.6256 15.0485 17.625 14.6627 17.625 14.2083H16.125ZM14.2083 17.625C14.6627 17.625 15.0485 17.6256 15.3641 17.5998C15.6884 17.5733 16.0039 17.5153 16.3055 17.3616L15.6245 16.0251C15.5696 16.0531 15.4735 16.0859 15.2419 16.1048C15.0016 16.1244 14.6874 16.125 14.2083 16.125V17.625ZM16.0251 15.6245C15.9372 15.797 15.797 15.9372 15.6245 16.0251L16.3055 17.3616C16.7602 17.1299 17.1299 16.7602 17.3616 16.3055L16.0251 15.6245ZM17.625 5.79167C17.625 5.33733 17.6256 4.95147 17.5998 4.63593C17.5733 4.31163 17.5153 3.99613 17.3616 3.69452L16.0251 4.37551C16.0531 4.43042 16.0859 4.52654 16.1048 4.75808C16.1244 4.99837 16.125 5.31258 16.125 5.79167H17.625ZM14.2083 3.875C14.6874 3.875 15.0016 3.87558 15.2419 3.89522C15.4735 3.91413 15.5696 3.94693 15.6245 3.97491L16.3055 2.6384C16.0039 2.48472 15.6884 2.42669 15.3641 2.4002C15.0485 2.37442 14.6627 2.375 14.2083 2.375V3.875ZM17.3616 3.69452C17.1299 3.2398 16.7602 2.87009 16.3055 2.6384L15.6245 3.97491C15.797 4.0628 15.9372 4.20303 16.0251 4.37551L17.3616 3.69452ZM3.875 5.79167C3.875 5.31258 3.87558 4.99837 3.89522 4.75808C3.91413 4.52654 3.94693 4.43042 3.97491 4.37551L2.6384 3.69452C2.48472 3.99613 2.42669 4.31163 2.4002 4.63593C2.37442 4.95147 2.375 5.33733 2.375 5.79167H3.875ZM5.79167 2.375C5.33733 2.375 4.95147 2.37442 4.63593 2.4002C4.31163 2.42669 3.99613 2.48472 3.69452 2.6384L4.37551 3.97491C4.43042 3.94693 4.52654 3.91413 4.75808 3.89522C4.99837 3.87558 5.31258 3.875 5.79167 3.875V2.375ZM3.97491 4.37551C4.06279 4.20303 4.20303 4.06279 4.37551 3.97491L3.69452 2.6384C3.2398 2.87009 2.87009 3.2398 2.6384 3.69452L3.97491 4.37551ZM9.25 3.125V8.125H10.75V3.125H9.25ZM10 7.375H3.125V8.875H10V7.375ZM9.25 8.125V11.875H10.75V8.125H9.25ZM9.25 11.875V16.875H10.75V11.875H9.25ZM10 12.625H16.875V11.125H10V12.625Z"
              fill="#5E6678"/>
            </svg>
            <BaseText type="label" size="sm" class="text-text-muted">
              Add Boards
            </BaseText>
          </div>
        </BaseButton>
        <!-- Divider -->
        <div class="h-6 border-r border-border-normal mx-3" />
        <BaseButton text @click="showBulkAddTagsModal = true">
          <div class="flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <g id="tag">
                <path id="Icon" d="M16.6592 9.47812L15.8647 5.15224C15.7612 4.58899 15.3205 4.14826 14.7572 4.0448L10.4313 3.25025C9.98531 3.16833 9.5275 3.31088 9.20684 3.63155L3.62323 9.21516C3.08457 9.75381 3.08457 10.6271 3.62323 11.1658L8.74366 16.2862C9.28231 16.8249 10.1556 16.8249 10.6943 16.2862L16.2779 10.7026C16.5986 10.382 16.7411 9.92415 16.6592 9.47812Z"
                stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path id="Icon_2" d="M12.0834 7.25C11.7152 7.25 11.4167 7.54848 11.4167 7.91667C11.4167 8.28486 11.7152 8.58333 12.0834 8.58333C12.4516 8.58333 12.7501 8.28486 12.7501 7.91667C12.7501 7.54848 12.4516 7.25 12.0834 7.25Z"
                fill="#5E6678" stroke="#5E6678" stroke-width="0.5"/>
              </g>
            </svg>
            <BaseText type="label" size="sm" class="text-text-muted">
              Add Tags
            </BaseText>
          </div>
        </BaseButton>
        <!-- Divider -->
        <div class="h-6 border-r border-border-normal mx-3" />
        <!-- Delete -->
        <BaseButton text @click="showConfirmBulkDeleteModal = true">
          <div class="flex items-center gap-1.5">
            <img src="../../assets/icons/trash.svg">
            <BaseText type="label" size="sm" class="text-secondary-red-100">
              Delete
            </BaseText>
          </div>
        </BaseButton>
      </div>

      <!-- MODALS -->
      <BulkAddBoardsModal
        v-if="showBulkAddBoardsModal"
        :selected-ads="selectedAds"
        :advertisements="advertisements"
        @updateAds="updateAds"
        @close="showBulkAddBoardsModal = false"
      />
      <BulkAddTagsModal
        v-if="showBulkAddTagsModal"
        :selected-ads="selectedAds"
        :advertisements="advertisements"
        @updateAds="updateAds"
        @close="showBulkAddTagsModal = false"
      />
      <ConfirmBulkDeleteModal
        v-if="showConfirmBulkDeleteModal === true || loadingDelete"
        :loading="loadingDelete"
        :selected-ads="selectedAds"
        @close="showConfirmBulkDeleteModal = false"
        @confirm="deleteSelectedAds"
      />
      <TutorialModal
        v-if="showTutorialModal"
        @close="showTutorialModal = false"
      />
      <ManageBoardModal
        v-if="showManageBoardModal"
        :board="selectedBoard"
        @close="showManageBoardModal = false"
        @save="reloadBoard"
      />
      <BoardToFigmaModal 
        v-if="showFigmaModal"
        :board="board"
        :adImages="figmaBoardImages"
        :adCount="adCount"
        @close="showFigmaModal = false"
      />

      <!-- Infinite loader -->
      <infinite-loading
        v-if="advertisements.length"
        :identifier="infiniteId"
        @infinite="getMoreAds"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </template>
  </MainLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import firebase from '@/api/config/FirebaseConfig'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import InfiniteLoading from 'vue-infinite-loading'
import { copyTextAsync } from '@/utils/clipboard'

// Components
import MainLayout from '../../layouts/MainLayout'
import RouteSwitcherDropdown from '../../components/library/RouteSwitcherDropdown.vue'
import SearchBar from '../../components/search/SearchBar.vue'
import FilterTopBar from '../../components/search/FilterTopBar.vue'
import AdvertisementList from '../../components/advertisements/AdvertisementList'
import AdvertismentTableView from '../../components/advertisements/AdvertismentTableView'
import LibraryEmptyState from '../../components/library/LibraryEmptyState.vue'

// Modals
import ManageBoardModal from '../../components/boards/ManageBoardModal'
import TutorialModal from '../../components/advertisements/TutorialModal'
import FolderModal from '../../components/sidebar/FolderModal.vue'
import BulkAddTagsModal from '../../components/advertisements/BulkAddTagsModal.vue'
import ConfirmBulkDeleteModal from '../../components/globals/ConfirmBulkDeleteModal.vue'
import BulkAddBoardsModal from '../../components/advertisements/BulkAddBoardsModal.vue'
import BoardToFigmaModal from '../../components/library/BoardToFigmaModal.vue'

// Icons
import HashtagIcon from '../../components/globals/Icons/HashtagIcon.vue'
import FolderIcon from '../../components/globals/Icons/SpyderV2Icons/FolderIcon.vue'
import BoardIcon from '../../components/globals/Icons/BoardIcon.vue'
import BriefIcon from '../../components/globals/Icons/BriefIcon'
import ChevronGrabberIcon from '../../components/globals/Icons/ChevronGrabberIcon.vue'
import ShareIcon from '../../components/globals/Icons/ShareIcon.vue'
import ChevronIcon from '../../components/globals/Icons/ChevronIcon.vue'
import EditIcon from '../../components/globals/Icons/EditIcon.vue'
import FigmaIcon from '../../components/globals/Icons/FigmaIcon.vue'
import PlayIcon from '../../components/globals/Icons/PlayIcon.vue'

export default {
  name: 'BoardsDetailsView',
  mixins: [clickaway],
  components: {
    InfiniteLoading,
    MainLayout,
    RouteSwitcherDropdown,
    SearchBar,
    FilterTopBar,
    AdvertisementList,
    AdvertismentTableView,
    LibraryEmptyState,
    ManageBoardModal,
    TutorialModal,
    FolderModal,
    BulkAddTagsModal,
    ConfirmBulkDeleteModal,
    BulkAddBoardsModal,
    BoardToFigmaModal,
    HashtagIcon,
    FolderIcon,
    BoardIcon,
    BriefIcon,
    ChevronGrabberIcon,
    ShareIcon,
    ChevronIcon  
  },
  data () {
    return {
      // Data States
      board: {},
      folder: {},
      advertisements: [],
      niches: [],
      textSearch: '',
      infiniteId: +new Date(),
      lastDocId: null,
      moreDropdownOptions: [],
      adCount: 0,

      // Filter States
      activeFilters: {},
      sortOrder: [{ name: 'Newest', value: 'desc' }],
      docOrder: 'desc',

      // UI States
      adView: 'grid',
      loadingMoreAds: false,
      loadingAdvertisements: true,
      allAdsLoaded: false,
      showMoreDropdown: false,
      showFigmaModal: false,
      figmaBoardImages: [],

      // Route switcher states
      loadingRouteSwitcher: true,
      routeSwitcherWidth: '308px',
      boardRoutes: [],
      boardSwitcherExpanded: false,

      // Modal States
      selectedBoard: {},
      showTutorialModal: false,
      showManageBoardModal: false,
      manualUpload: false,

      // Bulk Actions
      selectedAds: [],
      showBulkAddBoardsModal: false,
      showBulkAddTagsModal: false,
      showConfirmBulkDeleteModal: false,
      loadingDelete: false
    }
  },
  computed: {
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('AuthModule', ['getUserEmail', 'getUserName', 'getTeam']),
    ...mapGetters('BriefsModule', ['getIsInspoMode']),
    ...mapGetters('BoardsModule', ['getBoards', 'getFolders']),
    ...mapGetters('TagsModule', ['getTags']),
    ...mapGetters('MiscModule', ['getTheme']),
    ...mapGetters('RouterModule', ['getLastRoute']),
    filtersApplied () {
      return Object.keys(this.activeFilters).length > 0 || this.textSearch.length > 0
    }
  },
  created () {
    this.moreDropdownOptions = [
      { name: 'Edit', icon: EditIcon, action: this.handleEditBoard },
      // { name: 'Copy to Figma', icon: FigmaIcon, action: () => { this.showFigmaModal = true } },
      { name: 'Watch Tutorial', icon: PlayIcon, action: () => { this.showTutorialModal = true } }
    ]
  },
  async mounted () {
    // Set initial top bar styles
    this.$refs.filterBar.style.position = 'sticky'
    this.$refs.filterBar.style.top = '16px'
    this.$refs.filterBar.style.zIndex = '40000'

    this.sortOrder = this.textSearch.length
        ? [{ name: 'Most Relevant', value: 'relevance' }]
        : [{ name: 'Newest', value: 'desc' }]

    this.prepareRouteSwitcher()
    this.fetchAdvertisements()
    this.getBoardAdCount()
    this.niches = await this.getNiches()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.computeRouteSwitcherWidth)
  },
  methods: {
    // ================================================================================
    // ================================= DATA METHODS =================================
    // ================================================================================
    async deleteSelectedAds () {
      this.showConfirmBulkDeleteModal = false

      try {
        this.loadingDelete = true
        const deleteResponse = await FirebaseAPI.Advertisements.deleteAll(this.selectedAds)

        this.$showAlert({
          type: 'success',
          message: `${deleteResponse?.deletedAdIds?.length} ads deleted successfully!`
        })

        if (deleteResponse?.newStorageUsed) {
          this.SET_USER({ ...this.getUser, storageUsed: deleteResponse.newStorageUsed })
        }

        // local update
        this.removeAds(deleteResponse?.deletedAdIds)

        this.$showAlert({
          message: `${deleteResponse?.deletedCount} ads deleted successfully!`,
          type: 'success'
        })

        // Remove selected ads menu
        this.selectedAds = []
      } catch (error) {
        console.log(error)
        this.$showAlert({
          message: 'Error deleting ads',
          type: 'error'
        })
      } finally {
        this.loadingDelete = false
      }
    },
    async getNiches () {
      // Setup
      const db = firebase.firestore()
      const snapshot = await db.collection('niches').get()
      const newDocs = snapshot.docs?.map((doc) => {
        const docData = {
          ...(doc.data() || [])
        }
        return { name: docData.niche, icon: docData.emoji }
      })

      const sortedDocs = newDocs.sort((a, b) => (a.name > b.name ? 1 : -1))
      const otherIndex = sortedDocs.findIndex((d) => d.name === 'Other')
      const otherNiche = sortedDocs.splice(otherIndex, 1)

      sortedDocs.push(otherNiche[0])
      return sortedDocs
    },
    async prepareRouteSwitcher () {
      // Get the current board object
      const currentBoard = await this.getCurrentBoard()
      this.board = { ...currentBoard, name: currentBoard.name?.replace(/#/g, "") || currentBoard.name, icon: BoardIcon }
      // Determine the current folder
      const currentFolder = this.getFolders.find(folder => folder?.boardIds?.includes(currentBoard.id))
        || { id: null, name: 'Default Folder' }
      this.folder = { ...currentFolder, icon: FolderIcon }

      // ==== CONSTRUCT THE BOARD ROUTE OPTIONS ====
      let boardRoutes
      if (!currentFolder.id) { // If we're in the default folder, get all boards that aren't in any folders
        boardRoutes = this.getBoards.filter(board =>
          board.id !== currentBoard.id && !this.getFolders.some(folder => folder?.boardIds?.includes(board.id))
        )
      } else { // Otherwise, get all the other boards in the current folder (if there are any)
        boardRoutes = currentFolder?.boardIds?.length
          ? currentFolder.boardIds.map(boardId => this.getBoards.find(board => board.id === boardId)).filter(board => board)
          : []
      }
      boardRoutes = boardRoutes.map(board => ({
        ...board,
        name: board.name?.replace(/#/g, "") || board.name,
        icon: BoardIcon,
        action: () => this.$router.push({ name: 'BoardsDetailsView', params: { id: board.id }})
      }))
      
      // Assign the states
      this.boardRoutes = boardRoutes
      this.loadingRouteSwitcher = false

      // Compute the alloted width for the route switcher
      this.$nextTick(() => {
        this.computeRouteSwitcherWidth()
        window.addEventListener('resize', this.computeRouteSwitcherWidth)
      })
    },
    async getBoardAdCount () {
      // TODO: Atm, we are only using count in the figma popup. If we want to display on the page we should
      // bundle it with the main board request function (ALSO ADD AN ENDPOINT FOR GETTING A SINGLE BOARD'S AD COUNT)
      const boardId = this.$route.params.id
      const adCounts = await ForeplayAPI.Ads.getBoardsAdCount(this.getTeam ? this.getTeam.id : undefined)
      this.adCount = adCounts.find(adCount => adCount.key === boardId)?.doc_count || 0
    },
    async getCurrentBoard () {
      // Fetch and return the board corresponding to this route
      try {
        if (!this.$route.params.id) throw new Error('No board ID provided')
        return await FirebaseAPI.Boards.getByID(this.$route.params.id)
      } catch (error) {
        console.error(error)
        this.$showAlert({
          message: 'Failed to retrieve board data',
          type: 'error'
        })
        this.$router.push({ name: 'LibraryView' })
      }
    },
    async fetchAdvertisements (load = true, refresh = false) {
      this.loadingAdvertisements = load
      this.advertisements = []
      if (refresh) this.lastDocId = null

      const boardId = this.$route.params.id

      try {
        if (boardId) {
          const { results, nextPage } = await ForeplayAPI.Ads.getBoardAds(
            boardId,
            null,
            {
              ...this.activeFilters,
              textSearch: this.textSearch,
              sort: this.sortOrder[0].value,
            }
          )
          this.lastDocId = nextPage
          this.advertisements = results
          this.updateFigmaBoardImages()
        } else {
          this.$router.push({ name: 'LibraryView' })
          this.$showAlert({
            message: 'Board not found',
            type: 'error'
          })
        }
      } catch (e) {
        console.log(e)
        this.$showAlert({
          message: e,
          type: 'error'
        })
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'))
        }, 100)
      }
    },
    async getMoreAds ($state) {
      this.loadingMoreAds = true
      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        this.allAdsLoaded = true
        this.loadingMoreAds = false
        return
      }

      const boardId = this.$route.params.id

      const { results, nextPage } = await ForeplayAPI.Ads.getBoardAds(
        boardId,
        this.lastDocId,
        {
          ...this.activeFilters,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value,
        }
      )

      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        this.loadingMoreAds = false
        return
      }

      this.advertisements = this.advertisements.concat(results.map((ad) => {
        return { ...ad, tags: ad.tags || [] } // Fix deep prop watch issues
      }))
      this.loadingMoreAds = false
      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    // Handle Save Board
    async reloadBoard () {
      const board = await FirebaseAPI.Boards.getByID(this.board.id)
      if (this.board.id === board.id) {
      } else if (board) {
        this.board = board
        this.$router.push({
          name: 'BoardsDetailsView',
          params: { id: board.name }
        })
      } else {
        this.$router.push({ name: 'BoardsView' })
      }
    },
    copyShareLink () {
      copyTextAsync(async () => {
        const sharedID = await FirebaseAPI.Boards.createShared(this.board.id)
        return process.env.VUE_APP_BASE_URL + '/share/boards/' + encodeURIComponent(sharedID)
      })
        .then(() => {
          this.$showAlert({
            message: 'Copied share link to clipboard!',
            type: 'success'
          })
        })
        .catch((err) => {
          console.log(err)
          this.$showAlert({
            message: 'Uh oh, error copying link',
            type: 'error'
          })
        })
    },
    updateAds (adsToUpdate) {
      const duplicateAds = [...this.advertisements]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.advertisements = duplicateAds
    },
    removeAds (adsToRemove) {
      const removeIds = new Set(adsToRemove.map(adId => adId));
      this.advertisements = this.advertisements.filter(ad => !removeIds.has(ad.id));
    },
    updateRouteQuery () {
      if (!this.textSearch.length) {
        window.history.replaceState({}, '', this.$route.path)
        return
      }
      // Update the route's search query parameter with the current textSearch value
      const params = new URLSearchParams(window.location.search)
      params.set('search', this.textSearch)
      const newUrl = `${this.$route.path}?${params.toString()}`
      window.history.replaceState({}, '', newUrl)
    },

    // ================================================================================
    // ================================ EVENT HANDLERS ================================
    // ================================================================================

    handleSearchSubmit () {
      this.updateRouteQuery()
      this.sortOrder = this.textSearch.length
        ? [{ name: 'Most Relevant', value: 'relevance' }]
        : [{ name: 'Newest', value: 'desc' }]
      this.fetchAdvertisements()
    },
    navigateBack () {
      // If there is a previous history entry, go back
      if (this.getLastRoute) {
        this.$router.go(-1)
      } else {
        // Otherwise, go to the home page
        this.$router.push({ name: 'LibraryView' })
      }
    },
    handleCreateBoard () {
      this.selectedBoard = {}
      this.showManageBoardModal = true
    },
    handleEditBoard () {
      this.selectedBoard = this.board
      this.showManageBoardModal = true
    },
    handleClipboardCopy () {
      // Track Board Shared
      window.analytics.track('Board Shared', {
        boardName: this.board.name,
        boardId: this.board.id
      })
      this.$showAlert({
        message: 'Copied share link to clipboard!',
        type: 'success'
      })
    },
    // Handle Clear Filters
    async clearFilters () {
      const filterTopBar = this.$refs.filterTopBar
      filterTopBar.clearAllFilters()
      this.textSearch = ''

      await this.fetchAdvertisements()

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },

    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    updateFigmaBoardImages () {
      let images = []
      let i = 0
      while (images.length < 4 && i < this.advertisements.length) {
        const ad = this.advertisements[i]
        const img = ad.image || ad.thumbnail || ad.cards?.[0]?.image || ad.cards?.[0]?.thumbnail
        if (img) images.push(img)
        i++
      }
      this.figmaBoardImages = images
    },
    computeRouteSwitcherWidth () {
      const toolbarLeft = this.$refs.toolbar.getBoundingClientRect().left
      const searchLeft = this.$refs.searchBar.getBoundingClientRect().left
      const width = searchLeft - toolbarLeft - 16
      this.routeSwitcherWidth = `${width}px`
    },
    onScroll (e, position) {
      const marginValue =
        position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding =
        position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5

      this.$refs.filterBar.style.marginLeft = `${marginValue}px`
      this.$refs.filterBar.style.marginRight = `${marginValue}px`
      this.$refs.filterBar.style.marginTop = `${marginValue}px`
      this.$refs.filterBar.style.borderRadius = `${rounding}px`

      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue
          ? position.scrollTop / 15
          : maxValue
      }

      if (position.scrollTop) {
        this.$refs.filterBar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          15
        )}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          7
        )}px ${shadowSection(
          10
        )}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          7
        )}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          3
        )}px ${shadowSection(5)}px`
        // this.$refs.filterBar.style.boxShadow = `rgba(58, 111, 251, 0.4) -${shadowSection(5)}px ${shadowSection(5)}px, rgba(58, 111, 251, 0.3) -${shadowSection(10)}px ${shadowSection(10)}px, rgba(58, 111, 251, 0.2) -${shadowSection(15)}px ${shadowSection(15)}px, rgba(58, 111, 251, 0.1) -${shadowSection(20)}px ${shadowSection(20)}px, rgba(58, 111, 251, 0.05) -${shadowSection(25)}px ${shadowSection(25)}px`
      } else {
        this.$refs.filterBar.style.boxShadow = null
      }
      if (marginValue === 16) {
        this.$refs.filterBar.style.position = 'sticky'
        this.$refs.filterBar.style.top = '16px'
        this.$refs.filterBar.style.zIndex = '40000'
      }
    },
  }
}
</script>

<style scoped>
.shadow-elem {
  display: flex;
  justify-content: center;
  margin-left: 5%;
  width: 40%;
}

.moveRight {
  animation: moveRightAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}
.moveLeft {
  animation: moveLeftAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}
.startRight {
  transform: translateX(50%);
}
@keyframes moveRightAnimation {
  from {transform: translateX(0%);}
  to {transform: translateX(50%);}
}

@keyframes moveLeftAnimation {
  from {transform: translateX(50%);}
  to {transform: translateX(0%);}
}

@property --share-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --share-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.share-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--share-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--share-btn-opacity-2)) 100%), #006BFF;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: --share-btn-opacity-1 100ms ease-in-out, --share-btn-opacity-2 100ms ease-in-out;
  transform-origin: center;
}
.share-btn:hover {
  --share-btn-opacity-1: 0.24;
  --share-btn-opacity-2: 0;
}
.share-btn:active {
  --share-btn-opacity-1: 0;
  --share-btn-opacity-2: 0.24;
}

.more-dropdown {
  width: 154px;
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px #FFF;
  z-index: 50001;
}

.bottom-nav {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0px 8px 24px -12px rgba(2, 23, 59, 0.36),
    0px 1px 2px rgba(164, 172, 185, 0.16),
    0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  backdrop-filter: blur(5px);

  z-index: 57;
  transform: translateX(-50%);

  /* 138 is half of the sidebar width */
  left: calc(50% + 138px);
  bottom: 38px;

  border-radius: 12px;
}

.appear-animation {
  animation: appear 300ms ease-in;
  animation-iteration-count: 1;
}
@keyframes appear {
  0% {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
